/**
 * Digital Files (Linked-Files) API hooks — TanStack Query wrappers.
 *
 * Phase 13: files are tracked by external URL. No upload, no download — just
 * link metadata against a film.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../config/api';

export const fileKeys = {
  all: ['digital-files'],
  account: (filters) => ['digital-files', 'account', filters],
  workspace: (wsId, filters) => ['digital-files', 'workspace', wsId, filters],
  film: (filmId, filters) => ['digital-files', 'film', filmId, filters],
  detail: (id) => ['digital-files', 'detail', id],
};

async function fetchFiles({ scope, workspace_id, film_id, file_type, scene_id }) {
  const params = { scope };
  if (workspace_id) params.workspace_id = workspace_id;
  if (film_id) params.film_id = film_id;
  if (file_type) params.file_type = file_type;
  if (scene_id) params.scene_id = scene_id;
  const { data } = await apiClient.get('/digital-files', { params });
  return data;
}

async function fetchFile(id) {
  const { data } = await apiClient.get(`/digital-files/${id}`);
  return data;
}

async function createFile(body) {
  const { data } = await apiClient.post('/digital-files', body);
  return data;
}

async function updateFile({ id, ...body }) {
  const { data } = await apiClient.patch(`/digital-files/${id}`, body);
  return data;
}

async function deleteFile(id) {
  await apiClient.delete(`/digital-files/${id}`);
}

/* — Query hooks — */
export function useDigitalFiles({ scope = 'account', workspace_id, film_id, file_type, scene_id } = {}) {
  const filters = { file_type, scene_id };
  const key =
    scope === 'film'
      ? fileKeys.film(film_id, filters)
      : scope === 'workspace'
        ? fileKeys.workspace(workspace_id, filters)
        : fileKeys.account(filters);

  return useQuery({
    queryKey: key,
    queryFn: () => fetchFiles({ scope, workspace_id, film_id, file_type, scene_id }),
    enabled:
      scope === 'account' ||
      (scope === 'workspace' && !!workspace_id) ||
      (scope === 'film' && !!film_id),
  });
}

export function useDigitalFile(id) {
  return useQuery({
    queryKey: fileKeys.detail(id),
    queryFn: () => fetchFile(id),
    enabled: !!id,
  });
}

/* — Mutations — */
export function useCreateFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createFile,
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.all }),
  });
}

export function useUpdateFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateFile,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: fileKeys.all });
      qc.setQueryData(fileKeys.detail(data.id), data);
    },
  });
}

export function useDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteFile,
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.all }),
  });
}
